import { RefObject, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Utils } from "helpers/utils";
import { useRedux } from "hooks";
import Loader from "components/Loader";
import UserAvatar from "components/UserAvatar";
import { TaskStatus, WpTitle } from "../../../redux/workplace/wpTypes";
import { setActivePostId2 } from "../../../redux/workplace/wpActions";
import { WpModes } from "../../../constants";
import { APIClient } from "../../../api/apiCore";
import * as urls from "../../../api/urls"

// 20261021: sub-task picker popup, shared by the task list (fa-list-ol icon on a title)
// and the task header ("Sub-tasks (n)" badge on a main task).
// Position + click-outside are owned by the caller's useCursorPopup (popRef / pos).
interface SubTaskPopupProps {
  parentTaskId: string;
  popRef: RefObject<HTMLDivElement>;
  pos: { x: number; y: number };
  onClose: () => void;
}

const SubTaskPopup = ({ parentTaskId, popRef, pos, onClose }: SubTaskPopupProps) => {
  const { dispatch } = useRedux();
  const [subTasks, setSubTasks] = useState<WpTitle[] | null>(null);
  const [subTaskLoading, setSubTaskLoading] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    setSubTasks(null);
    setSubTaskLoading(true);
    const api = new APIClient
    api.fnPostForm(urls.WP_LOAD_SUB_TASK, { parentTaskId })
      .then(rs => {
        if (cancelled) return;
        setSubTasks(rs);
        setSubTaskLoading(false);
      })
      .catch(e2 => {
        if (cancelled) return;
        setSubTaskLoading(false);
        Utils.HandleException(e2, true);
      });
    return () => { cancelled = true; };
  }, [parentTaskId]);

  const onClickSubTask = (data: WpTitle) => {
    // 20260828 a sub-task comes from the /wp-load-sub-task popup, so it is NOT in titlesTk -
    // without this guard clearActivePostOnTitleChange wipes it on the next titlesTk change
    // (same escape hatch ApprovalList.tsx and notificationRouting.ts already use).
    Utils.SetRedirectPostId(WpModes.Task, data.postId);
    dispatch(setActivePostId2(WpModes.Task, data.postId, data.title, data.abbrName, data.abbrFore, data.abbrBack, "sub task", data));
    onClose();
  }

  // portal to <body>: .workplace-header has backdrop-filter, which makes it the containing block
  // for position:fixed children - rendered inside it the popup gets clipped to the header.
  return createPortal(
    <div ref={popRef} className="wp-conver-content" style={{ zIndex: "1000", minWidth: "200px", minHeight: "200px", maxWidth: "calc(100vw - 60px)", position: "fixed", top: pos.y, left: pos.x }}>
      {subTaskLoading && (<Loader />)}
      <div className="d-flex " style={{ gap: "8px", flexDirection: "column" }}>
        {/* 20261021: list icon uses the total count, the list only the sub-tasks this user may open (sql-log Step 29) */}
        {!subTaskLoading && subTasks?.length === 0 && (<div className="text-muted p-2">ไม่มีงานย่อยที่ท่านมีสิทธิ์ดู</div>)}
        {!subTaskLoading && subTasks?.map((task: WpTitle, index: number) => {
          return (
            <div key={index} className="hover hand d-flex justify-content-between align-items-center" onClick={(e) => onClickSubTask(task)}>
              <div className="d-flex align-items-center">
                <UserAvatar profileImage={task.abbrName} abbrName="" size="xxs" />
                {task.title}
              </div>
              {task.approveStatus && task.approveStatus.trim() !== "" && (
                <i className={Utils.GetDSignApproveStatusIconClass(task.approveStatus) + " me-1"} title={Utils.GetDSignApproveStatusText(task.approveStatus)}></i>
              )}
              <span className={`badge-task-status badge-task-status-${TaskStatus[task.taskStatus ?? TaskStatus.ToDo].toLowerCase()}`}>
                {Utils.GetTaskStatusIcon(task.taskStatus ?? TaskStatus.ToDo)}
                {Utils.GetTaskStatusText(task.taskStatus ?? TaskStatus.ToDo)}
              </span>
            </div>
          )
        })}
      </div>
    </div>,
    document.body
  );
}

export default SubTaskPopup;
